/**
 * Color utilities for calculating contrast and determining text colors
 * Based on WCAG 2.0 accessibility guidelines
 */

/**
 * Calculate relative luminance of a hex color
 * Based on WCAG 2.0 formula
 */
export function getLuminance(hexColor: string): number {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const [rs, gs, bs] = [r, g, b].map(c => 
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine if text should be light or dark based on background
 * Returns 'light' for white text, 'dark' for dark text
 */
export function getContrastTextMode(backgroundColor: string): 'light' | 'dark' {
  const luminance = getLuminance(backgroundColor);
  return luminance > 0.4 ? 'dark' : 'light';
}

/**
 * Get text mode based on average luminance of gradient colors
 * Uses weighted average giving more importance to the middle color
 */
export function getGradientTextMode(from: string, via: string, to: string): 'light' | 'dark' {
  // Weighted average: via (middle) has more visual prominence
  const avgLuminance = (
    getLuminance(from) * 0.25 + 
    getLuminance(via) * 0.5 + 
    getLuminance(to) * 0.25
  );
  return avgLuminance > 0.35 ? 'dark' : 'light';
}

/**
 * Lighten a hex color by mixing it with white
 * amount: 0 = original color, 1 = pure white
 */
export function lightenColor(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const nr = Math.round(r + (255 - r) * amount);
  const ng = Math.round(g + (255 - g) * amount);
  const nb = Math.round(b + (255 - b) * amount);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
}
